<script setup lang="ts">
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import BackToTop from '@components/util/BackToTop.vue';
import LandingBanners from '@components/ui/index&flight-info/LandingBanners.vue';
import InformationContainer from '@components/ui/auto-reserve&gift-card&index/InformationContainer.vue';
import { type BreadcrumbItem } from '@components/base/mr-breadcrumb';

const flightInfoStore = useFlightInfoStore();
const contentStore = useContentStore();
const { landingPageDescription, landingPageTitle, landingPageH1 } =
    storeToRefs(contentStore);
const route = useRoute();
const router = useRouter();
await useAsyncData(async () => {
    await Promise.all([
        flightInfoStore.fetchAirports(),
        contentStore.loadHomepageMessages(),
    ]);
    return true;
});
await useAsyncData(
    async () => {
        await contentStore.getPageData(route.path);
        return true;
    },
    { server: true, watch: [() => route.path] }
);
useSeoMeta({
    description: landingPageDescription,
    ogDescription: landingPageDescription,
    title: landingPageTitle,
});

definePageMeta({
    whiteLabel: 'mrbilit',
});
const isWebView = route.path.startsWith('/mobile');
const selectedAirport = ref<string>();
const flightType = ref<'all' | 'domestic' | 'international'>('all');
const flightTypeOptions = [
    { id: 'all', label: 'همه' },
    { id: 'domestic', label: 'داخلی' },
    { id: 'international', label: 'خارجی' },
];
const searchTerm = ref('');
const airports = computed(
    () =>
        flightInfoStore.airports.map(airport => ({
            id: airport.iataCode || '',
            label: airport.airportName || '',
        })) || []
);
const airport = computed(() => {
    if (route.params.airport) {
        return airports.value.find(apt => apt.id === route.params.airport);
    }
    return undefined;
});
watch(selectedAirport, val => {
    searchTerm.value = '';
    if (!val || route.params.airport === val) {
        return;
    }
    let baseRoute = '/flight-info';
    if (isWebView) {
        baseRoute += '-app';
    }
    router.replace(`${baseRoute}/${val}`);
});
watch(
    () => route.params,
    id => {
        selectedAirport.value = id.airport?.toString();
    },
    { immediate: true }
);
const breadcrumbItems = computed(() => {
    const result: BreadcrumbItem[] = [{ link: '/', title: 'مِستربلیط' }];
    result.push({
        link: '/plane-ticket',
        title: 'بلیط هواپیما',
    });
    result.push({
        link: '/flight-info',
        title: 'اطلاعات پرواز فرودگاه‌های کشور',
    });
    if (route.params.airport) {
        const airport = airports.value.find(
            apt => apt.id === route.params.airport
        );
        result.push({
            title: `اطلاعات پرواز ${airport?.label}` || '',
            link: route.path,
        });
    }
    return result;
});
</script>

<template>
    <div class="root">
        <BackToTop />
        <div class="header">
            <div class="header-content">
                <div class="title">
                    <div class="heading">
                        <h1>
                            {{
                                landingPageH1 ||
                                (airport
                                    ? `اطلاعات پرواز ${airport.label}`
                                    : 'اطلاعات پرواز فرودگاه‌های کشور')
                            }}
                        </h1>
                    </div>
                    <p>
                        {{
                            landingPageDescription ||
                            (airport
                                ? `استعلام وضعیت تابلو پرواز ${airport.label}`
                                : 'استعلام وضعیت تابلو پرواز تمامی فرودگاه‌ها')
                        }}
                    </p>
                </div>
                <img
                    src="~/assets/files/flight-info/pattern.svg"
                    alt="pattern"
                    class="pattern"
                />
            </div>
        </div>
        <div class="body">
            <div class="top-card">
                <LandingBanners class="banners-container" />
                <mr-fix>
                    <div class="search-box-wrapper">
                        <div class="search-box-sticky-wrapper">
                            <div class="search-box">
                                <mr-select
                                    v-model="selectedAirport"
                                    class="search-select"
                                    :options="airports"
                                    hide-description
                                    required
                                    placeholder="جستجو براساس فرودگاه"
                                />
                                <mr-select
                                    v-model="flightType"
                                    class="search-select"
                                    :options="flightTypeOptions"
                                    hide-description
                                    required
                                    placeholder="نوع پرواز"
                                />
                                <mr-input
                                    v-model="searchTerm"
                                    class="search-input"
                                    :class="{ hidden: !selectedAirport }"
                                    :icon="faSearch"
                                    placeholder="جستجوی هواپیمایی، مبدأ، مقصد، شماره پرواز"
                                    hide-description
                                />
                            </div>
                        </div>
                    </div>
                </mr-fix>
            </div>
            <div class="cards-box-container">
                <nuxt-page
                    :search="searchTerm"
                    :filter="flightType"
                    :is-web-view
                />
            </div>
        </div>
        <InformationContainer hide-busy-way />
        <mr-breadcrumb class="crumb" :items="breadcrumbItems" />
    </div>
</template>
<style lang="scss" scoped>
.root {
    .header {
        height: 270px;
        padding-top: 70px;
        @include mobile-only {
            height: 168px;
            padding-top: 0;
        }
        width: 100%;
        background-color: usePalette();
        display: flex;
        flex-direction: column;
        align-items: center;
        .header-content {
            max-width: $app-container-max-width;
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            .title {
                @include mobile-only {
                    width: 100%;
                }
                p {
                    margin: 0;
                    @include tg-l($color: usePalette($p-blue, $p-200));
                    text-align: center;
                }
                .heading {
                    display: flex;
                    margin-top: 0;
                    margin-bottom: 8px;
                    @include f-gap(3px);
                    justify-content: center;
                    h1,
                    .tg-display3-700 {
                        color: white;
                        text-align: center;
                        margin-top: 0;
                        margin-bottom: 0;
                    }
                }
            }
            .pattern {
                align-self: flex-end;
                @include mobile-only {
                    display: none;
                }
                margin-left: 80px;
                margin-bottom: 16px;
            }
        }
    }
    .body {
        .top-card {
            @include shadow();
        }
        .search-box-wrapper {
            width: 100%;
            background-color: white;
            padding-top: 16px;
            @include mobile-only {
                padding-top: 0;
            }
            .search-box-sticky-wrapper {
                background-color: white;
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                .search-box {
                    max-width: $app-container-max-width;
                    width: 100%;
                    padding-bottom: 16px;
                    @include mobile-only {
                        padding: 16px;
                    }
                    display: flex;
                    flex-wrap: wrap;

                    .search-select,
                    .search-input {
                        flex-basis: calc(33% - 8px);
                        max-width: calc(33% - 8px);
                        @include mobile-only {
                            flex-basis: 100%;
                            max-width: 100%;
                        }
                    }

                    .search-select {
                        margin-left: 8px;
                        @include mobile-only {
                            margin-left: 0;
                            margin-bottom: 12px;
                        }
                    }
                    .search-input {
                        margin-right: 8px;
                        @include mobile-only {
                            margin-right: 0;
                        }
                        &.hidden {
                            visibility: hidden;
                            display: block;
                            @include mobile-only {
                                visibility: visible;
                                display: none;
                            }
                        }
                    }
                }
            }
        }
        .cards-box-container {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
        }
    }
    .banners-container {
        padding-top: 16px;
    }
    .crumb {
        max-width: 1200px;
        width: 100%;
        margin-inline: auto;
    }
}
</style>
